import { AlertCircle, ClipboardCopy } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { INCIDENT_DESCRIPTION_MAX } from "../constants/paymentIncidents.constants";
import type { PaymentIncidentType } from "../types/paymentIncidents.types";

const FORM_ID = "open-payment-incident";
const TYPES: PaymentIncidentType[] = ["credit_not_applied", "status_not_updated", "other"];

export interface OpenIncidentValues {
  type: PaymentIncidentType;
  description: string;
}

export interface OpenIncidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: string;
  defaultType: PaymentIncidentType;
  /** Opens the incident; `thenReport` goes straight on to copying the report. Throws a message to show. */
  onSubmit: (values: OpenIncidentValues, thenReport: boolean) => Promise<void>;
}

/** What went wrong, in staff words: it becomes the first entry of the timeline and the report's "Qué pasó". */
export function OpenIncidentDialog({
  open,
  onOpenChange,
  provider,
  defaultType,
  onSubmit,
}: OpenIncidentDialogProps) {
  const { t } = useTranslations("paymentIncidents");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thenReport = useRef(true);
  const schema = useMemo(
    () =>
      z.object({
        type: z.enum(["credit_not_applied", "status_not_updated", "other"]),
        description: z.string().trim().min(1, t("open_validation_description")).max(INCIDENT_DESCRIPTION_MAX),
      }),
    [t],
  );
  const form = useZodForm(schema, { type: defaultType, description: "" });

  const submit = async (values: OpenIncidentValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ type: values.type, description: values.description.trim() }, thenReport.current);
      form.reset({ type: defaultType, description: "" });
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : t("open_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) setError(null);
        onOpenChange(next);
      }}
      size="lg"
      title={t("open_title")}
      description={t("open_description", { provider })}
      showCloseButton={!submitting}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("action_cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            form={FORM_ID}
            variant="secondary"
            disabled={submitting}
            onClick={() => {
              thenReport.current = false;
            }}
          >
            {t("open_only")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            form={FORM_ID}
            variant="primary"
            icon={ClipboardCopy}
            loading={submitting}
            disabled={submitting}
            onClick={() => {
              thenReport.current = true;
            }}
          >
            {t("open_and_report", { provider })}
          </ColocaButton>
        </>
      }
    >
      <ColocaForm id={FORM_ID} form={form} onSubmit={submit} className="space-y-5">
        <ColocaRadioGroupField
          name="type"
          label={t("open_field_type")}
          items={TYPES.map((type) => ({
            value: type,
            label: (
              <span className="flex flex-col">
                <span className="text-sm font-medium text-foreground">{t(`type_${type}`)}</span>
                <span className="text-xs text-muted-foreground">{t(`type_${type}_hint`, { provider })}</span>
              </span>
            ),
          }))}
          disabled={submitting}
        />
        <ColocaTextareaField
          name="description"
          label={t("open_field_description")}
          description={t("open_field_description_hint", { provider })}
          placeholder={t("open_field_description_placeholder")}
          rows={4}
          maxLength={INCIDENT_DESCRIPTION_MAX}
          count
          disabled={submitting}
        />
        {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      </ColocaForm>
    </ColocaDialog>
  );
}
